import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowRight,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Shield,
  Timer,
  Lightbulb,
  ChevronRight,
  Loader2,
  RefreshCw,
  Sparkles
} from 'lucide-react';

import { OutputsPreviewModal } from '../../components/admin/OutputsPreviewModal';
import { PublishValidationModal } from '../../components/admin/PublishValidationModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  incoming: { label: 'Incoming', color: 'bg-blue-100 text-blue-800', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  published: { label: 'Published', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  used: { label: 'Used', color: 'bg-purple-100 text-purple-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const gateIcons = {
  1: Shield,
  2: Timer,
  3: Lightbulb,
};

// Story Card for Pipeline
const PipelineStoryCard = ({ story, onAdvance, onReviewGate, token }) => {
  const [expanded, setExpanded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [validateOpen, setValidateOpen] = useState(false);

  const canAdvance = story.status !== 'rejected' && story.status !== 'used';
  const gates = story.gates || [];
  const isPublishStep = story.status === 'approved';

  const handleAdvanceClick = () => {
    if (isPublishStep) {
      setValidateOpen(true);
    } else {
      onAdvance(story.id);
    }
  };

  return (
    <Card className="mb-3 border border-gray-200 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {story.image_url && (
            <img 
              src={story.image_url} 
              alt={story.title}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              onError={(e) => {
                const fb = 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=200';
                if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
              }}
            />
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 text-sm line-clamp-1">
              {story.title}
            </h4>
            <p className="text-xs text-gray-500 line-clamp-1 mt-1">
              {story.caption}
            </p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <span>{story.country_name}</span>
              <span>•</span>
              <span>{story.theme}</span>
            </div>
          </div>
        </div>

        {/* Expand/Collapse */}
        <button 
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-3 text-xs text-[#07743F] font-medium flex items-center justify-center gap-1 hover:underline"
        >
          {expanded ? 'Hide Details' : 'View Details'}
          <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>

        {/* Expanded Details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            {/* Gates */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-2">Review Gates:</p>
              <div className="space-y-2">
                {gates.map((gate, idx) => {
                  const GateIcon = gateIcons[gate.gate_number] || Shield;
                  const isPassed = gate.status === 'passed';
                  const isFailed = gate.status === 'failed';
                  const isPending = gate.status === 'pending';
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded-lg text-xs ${
                        isPassed ? 'bg-green-50' : isFailed ? 'bg-red-50' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <GateIcon className={`w-4 h-4 ${
                          isPassed ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-gray-400'
                        }`} />
                        <span className="font-medium">Gate {gate.gate_number}: {gate.gate_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isPending && story.status !== 'rejected' && (
                          <>
                            <button
                              onClick={() => onReviewGate(story.id, gate.gate_number, 'passed')}
                              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                            >
                              Pass
                            </button>
                            <button
                              onClick={() => onReviewGate(story.id, gate.gate_number, 'failed')}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                            >
                              Fail
                            </button>
                          </>
                        )}
                        {isPassed && (
                          <Badge className="bg-green-100 text-green-800">Passed</Badge>
                        )}
                        {isFailed && (
                          <Badge className="bg-red-100 text-red-800">Failed</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <Button
                type="button"
                onClick={() => setPreviewOpen(true)}
                size="sm"
                variant="outline"
                data-testid={`preview-outputs-${story.id}`}
                className="w-full border-[#07743F] text-[#07743F] hover:bg-[#07743F] hover:text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Preview Generated Outputs
              </Button>
              {canAdvance && (
                <Button
                  onClick={handleAdvanceClick}
                  size="sm"
                  data-testid={`advance-${story.id}`}
                  className="w-full bg-[#07743F] hover:bg-[#065a32] text-white"
                >
                  {isPublishStep ? 'Publish Story' : 'Advance to Next Stage'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
      <OutputsPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        story={story}
        token={token}
      />
      <PublishValidationModal
        open={validateOpen}
        onClose={() => setValidateOpen(false)}
        story={story}
        token={token}
        onConfirm={() => onAdvance(story.id)}
      />
    </Card>
  );
};

// Pipeline Column
const PipelineColumn = ({ status, stories, onAdvance, onReviewGate, token }) => {
  const config = statusConfig[status] || statusConfig.incoming;
  const StatusIcon = config.icon;
  
  return (
    <div className="flex-1 min-w-[280px] max-w-[320px]">
      <div className={`rounded-t-xl p-3 ${config.color}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className="w-4 h-4" />
            <span className="font-semibold text-sm">{config.label}</span>
          </div>
          <Badge variant="secondary" className="bg-white/50">
            {stories.length}
          </Badge>
        </div>
      </div>
      <div className="bg-gray-50 rounded-b-xl p-3 min-h-[400px] max-h-[600px] overflow-y-auto">
        {stories.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No stories
          </div>
        ) : (
          stories.map(story => (
            <PipelineStoryCard
              key={story.id}
              story={story}
              onAdvance={onAdvance}
              onReviewGate={onReviewGate}
              token={token}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Main Pipeline Page
export const PipelinePage = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login', { state: { from: { pathname: '/admin/pipeline' } } });
      return;
    }
    fetchPipeline();
  }, [isAdmin, navigate]);

  const fetchPipeline = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pipeline`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPipeline(data);
      }
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleAdvance = async (storyId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stories/${storyId}/advance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPipeline();
      }
    } catch (error) {
      console.error('Failed to advance story:', error);
    }
  };

  const handleReviewGate = async (storyId, gateNumber, status) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stories/${storyId}/review-gate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          story_id: storyId,
          gate_number: gateNumber,
          status: status
        })
      });
      if (response.ok) {
        fetchPipeline();
      }
    } catch (error) {
      console.error('Failed to review gate:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPipeline();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
      </div>
    );
  }

  const columns = ['incoming', 'under_review', 'approved', 'published', 'used'];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Story Journey</h1>
              <p className="text-sm text-gray-600">
                Guide stories from arrival to impact
              </p>
            </div>
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="border-[#07743F] text-[#07743F]"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Pipeline Flow Indicator */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 overflow-x-auto">
            {columns.map((col, idx) => (
              <React.Fragment key={col}>
                <span className={`px-3 py-1 rounded-full whitespace-nowrap ${statusConfig[col]?.color}`}>
                  {statusConfig[col]?.label}
                </span>
                {idx < columns.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Pipeline Columns */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map(status => (
            <PipelineColumn
              key={status}
              status={status}
              stories={pipeline[status] || []}
              onAdvance={handleAdvance}
              onReviewGate={handleReviewGate}
              token={token}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="container mx-auto px-4 pb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">3-Gate Review System</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Gate 1: Safety & Truth</h4>
                  <p className="text-sm text-gray-600">Verify story accuracy and ensure content is safe for publication</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Timer className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Gate 2: Timing</h4>
                  <p className="text-sm text-gray-600">Assess relevance and appropriate timing for release</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Gate 3: Usefulness</h4>
                  <p className="text-sm text-gray-600">Evaluate impact potential and alignment with YEFFA goals</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PipelinePage;
